import { SUPPORTED_LOCALES } from './types';

/** 유니크 ID 규칙 (요구사항 8) */
export const HANDLE_MIN = 4;
export const HANDLE_MAX = 20;
export const HANDLE_PATTERN = /^[a-z0-9_.]+$/;

/** 예약어 — 시스템 경로·역할명·오타 방지에 사용 */
export const RESERVED_HANDLES = new Set([
  // 시스템/역할
  'admin',
  'administrator',
  'root',
  'superuser',
  'system',
  'official',
  'support',
  'help',
  'staff',
  'team',
  'moderator',
  'mod',
  'security',
  // 경로·서비스
  'api',
  'about',
  'settings',
  'privacy',
  'terms',
  'legal',
  'contact',
  'talk2nick',
  'talk2nIcK',
  'talk2nick_app',
  'firebase',
  'google',
  // 오타 유발
  'user',
  'users',
  'me',
  'my',
  'null',
  'undefined',
  'none',
  'anonymous',
  'na',
  'nul',
  'and',
  'the',
  'login',
  'logout',
  'signup',
  'signin',
  // 숫자 단독
  '0',
  '1',
  '123',
  '1234',
  '12345',
  'test',
  'temp',
]);

export type HandleValidationResult =
  { ok: true; normalized: string } | { ok: false; reason: HandleError };

export type HandleError =
  | 'empty'
  | 'too_short'
  | 'too_long'
  | 'invalid_chars'
  | 'leading_trailing_dot'
  | 'consecutive_dots'
  | 'reserved'
  | 'owner_only'
  | 'numeric_only';

/** 소문자 정규화 — Firestore 문서 ID와 조회 키로 사용 */
export function normalizeHandle(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * 유니크 ID 검증. 서버 Firestore Rules가 최종 방어선이지만,
 * 클라이언트 선검증으로 UX를 개선한다.
 */
export function validateHandle(input: string): HandleValidationResult {
  const normalized = normalizeHandle(input);

  if (normalized.length === 0) return { ok: false, reason: 'empty' };
  if (normalized.length < HANDLE_MIN) return { ok: false, reason: 'too_short' };
  if (normalized.length > HANDLE_MAX) return { ok: false, reason: 'too_long' };
  if (!HANDLE_PATTERN.test(normalized)) return { ok: false, reason: 'invalid_chars' };
  if (normalized.startsWith('.') || normalized.endsWith('.')) {
    return { ok: false, reason: 'leading_trailing_dot' };
  }
  if (normalized.includes('..')) return { ok: false, reason: 'consecutive_dots' };
  if (RESERVED_HANDLES.has(normalized)) return { ok: false, reason: 'reserved' };
  if (/^[0-9]+$/.test(normalized)) return { ok: false, reason: 'numeric_only' };

  return { ok: true, normalized };
}

export const HANDLE_ERROR_MESSAGES: Record<HandleError, string> = {
  empty: 'ID를 입력해 주세요.',
  too_short: `ID는 ${HANDLE_MIN}자 이상이어야 합니다.`,
  too_long: `ID는 ${HANDLE_MAX}자 이하여야 합니다.`,
  invalid_chars: '영문 소문자, 숫자, `_`, `.`만 사용할 수 있습니다.',
  leading_trailing_dot: 'ID의 앞뒤에는 `.`을 쓸 수 없습니다.',
  consecutive_dots: '`.`을 연속해서 쓸 수 없습니다.',
  reserved: '이미 사용 중인 ID입니다. 다른 ID를 선택해 주세요.',
  owner_only: '이 ID는 다른 계정에서 사용할 수 없습니다.',
  numeric_only: '숫자만으로는 사용할 수 없습니다.',
};

/** DM 1:1 보장용 키 — 양방향 어느 쪽에서 생성해도 동일해야 한다 */
export function buildDmKey(uidA: string, uidB: string): string {
  return [uidA, uidB].sort().join('_');
}

// ── 로케일 ────────────────────────────────────────────────────────────

const LOCALE_LABELS: Record<(typeof SUPPORTED_LOCALES)[number], string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '中文',
  vi: 'Tiếng Việt',
  es: 'Español',
};

/** 언어 선택 UI용 네이티브 라벨 (자기 언어로는 쓰지 않음) */
export function nativeLocaleLabel(locale: (typeof SUPPORTED_LOCALES)[number]): string {
  return LOCALE_LABELS[locale];
}

/**
 * 로케일 결정 순서: ① 사용자 설정 → ② 브라우저 언어 → ③ ko (PLAN 5-7장)
 */
export function resolveLocale(
  userSetting: string | undefined,
  navigatorLanguages: readonly string[] | undefined,
): (typeof SUPPORTED_LOCALES)[number] {
  if (userSetting && isSupportedLocale(userSetting)) return userSetting;

  for (const tag of navigatorLanguages ?? []) {
    const base = tag.toLowerCase().split('-')[0];
    if (base && isSupportedLocale(base)) return base;
  }
  return 'ko';
}

export function isSupportedLocale(value: string): value is (typeof SUPPORTED_LOCALES)[number] {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
